import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useAssessment } from "../context/AssessmentContext";
import { PageShell } from "../components/PageShell";
import { TopProgressBar } from "../components/TopProgressBar";
import { getCategories } from "../lib/scopeEngine";

export default function Questionnaire() {
  const navigate = useNavigate();
  const { client, answers, setAnswer } = useAssessment();
  const [step, setStep] = useState(0);

  const CATEGORIES_LIST = useMemo(() => getCategories(client.stage), [client.stage]);
  const current = CATEGORIES_LIST[step] || CATEGORIES_LIST[0];

  // Initialise slider defaults on entering a step (midpoint)
  useEffect(() => {
    current.questions.forEach((q) => {
      if (q.type === "slider" && (answers[q.id] === undefined || answers[q.id] === "")) {
        const mid = Math.floor(((q.min || 1) + (q.max || 10)) / 2);
        setAnswer(q.id, mid);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  const totalSteps = CATEGORIES_LIST.length;
  const progress = 8 + ((step + 1) / totalSteps) * 84; // approx 8..92

  const canAdvance = useMemo(() => {
    return current.questions.every((q) => {
      if (q.optional) return true;
      const v = answers[q.id];
      if (q.type === "slider") return v !== undefined && v !== null && v !== "";
      return v !== undefined && v !== null && v !== "";
    });
  }, [current, answers]);

  const next = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/results");
    }
  };

  const back = () => {
    if (step > 0) setStep(step - 1);
    else navigate("/client");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <PageShell progressBar={<TopProgressBar value={progress} />} max="max-w-3xl">
      <div data-testid="questionnaire-page">
        {/* Step header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#1FA8E0] mb-2">
              {String(step + 1).padStart(2, "0")} / {String(totalSteps).padStart(2, "0")} · {current.label}
            </div>
            <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0A1A2F] leading-tight tracking-tight max-w-xl">
              {current.intro}
            </h1>
          </div>
          <div className="hidden md:flex items-center gap-1">
            {CATEGORIES_LIST.map((c, i) => (
              <span
                key={c.id}
                className={`h-[3px] w-5 rounded-sm transition-all duration-300 ${
                  i < step ? "bg-[#0A1A2F]" : i === step ? "bg-[#1FA8E0]" : "bg-[#E5E7EB]"
                }`}
              />
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6"
          >
            {current.questions.map((q) => (
              <QuestionCard
                key={q.id}
                q={q}
                value={answers[q.id]}
                onChange={(v) => setAnswer(q.id, v)}
              />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Nav */}
        <div className="mt-12 flex items-center justify-between pt-6 border-t border-[#E5E7EB]">
          <button
            data-testid="questionnaire-back-btn"
            onClick={back}
            className="inline-flex items-center gap-2 text-[#6B7280] hover:text-[#0A1A2F] transition-colors text-sm"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            Back
          </button>
          <button
            data-testid="questionnaire-next-btn"
            onClick={next}
            disabled={!canAdvance}
            className="inline-flex items-center gap-3 bg-[#0A1A2F] hover:bg-[#2F5D8C] disabled:bg-[#9CA3AF] disabled:cursor-not-allowed text-white px-8 py-3 rounded-sm transition-all duration-300"
          >
            <span className="text-xs tracking-[0.2em] uppercase">
              {step === totalSteps - 1 ? "See Results" : "Continue"}
            </span>
            <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </PageShell>
  );
}

const QuestionCard = ({ q, value, onChange }) => {
  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] elevated-card p-7 md:p-9" data-testid={`question-${q.id}`}>
      <div className="font-display font-bold text-xl md:text-2xl text-[#0A1A2F] leading-snug mb-6">
        {q.prompt}
        {q.optional && <span className="ml-2 text-[11px] tracking-[0.2em] uppercase text-[#9CA3AF] align-middle">Optional</span>}
      </div>

      {q.type === "choice" && (
        <div className="space-y-3">
          {q.options.map((opt) => {
            const selected = value === opt.value;
            return (
              <button
                key={opt.value}
                data-testid={`option-${q.id}-${opt.value}`}
                onClick={() => onChange(opt.value)}
                className={`w-full text-left flex items-center gap-4 px-5 py-4 rounded-sm border transition-all duration-200 ${
                  selected
                    ? "border-[#0A1A2F] bg-[#0A1A2F] text-white"
                    : "border-[#E5E7EB] bg-white hover:border-[#1FA8E0] hover:bg-[#F5F7FA] text-[#0A1A2F]"
                }`}
              >
                <span
                  className={`h-5 w-5 rounded-full flex items-center justify-center border ${
                    selected ? "bg-white border-white" : "border-[#E5E7EB]"
                  }`}
                >
                  {selected && <Check className="h-3 w-3 text-[#0A1A2F]" strokeWidth={3} />}
                </span>
                <span className="text-sm md:text-base">{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {q.type === "yesno" && (
        <div className="grid grid-cols-2 gap-3">
          {["yes", "no"].map((v) => {
            const selected = value === v;
            return (
              <button
                key={v}
                data-testid={`option-${q.id}-${v}`}
                onClick={() => onChange(v)}
                className={`px-5 py-4 rounded-sm border text-sm tracking-[0.2em] uppercase transition-all duration-200 ${
                  selected
                    ? "border-[#0A1A2F] bg-[#0A1A2F] text-white"
                    : "border-[#E5E7EB] bg-white hover:border-[#1FA8E0] text-[#0A1A2F]"
                }`}
              >
                {v}
              </button>
            );
          })}
        </div>
      )}

      {q.type === "slider" && (
        <div className="pt-2">
          <div className="flex items-center justify-between text-xs text-[#6B7280] mb-3">
            <span>{q.lowLabel}</span>
            <span className="text-[#0A1A2F] font-medium text-base">
              {value !== undefined && value !== "" ? value : q.min}
              <span className="text-[#9CA3AF] text-xs"> / {q.max}</span>
            </span>
            <span>{q.highLabel}</span>
          </div>
          <input
            data-testid={`slider-${q.id}`}
            type="range"
            min={q.min}
            max={q.max}
            step={q.step || 1}
            value={value !== undefined && value !== "" ? value : Math.floor((q.min + q.max) / 2)}
            onChange={(e) => onChange(Number(e.target.value))}
            className="eyb-slider"
          />
          <div className="flex justify-between mt-2">
            {Array.from({ length: q.max - q.min + 1 }).map((_, i) => (
              <span key={i} className="text-[10px] text-[#9CA3AF]">{q.min + i}</span>
            ))}
          </div>
        </div>
      )}

      {q.type === "text" && (
        <textarea
          data-testid={`textarea-${q.id}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={q.placeholder || "Share a quick note..."}
          rows={3}
          className="w-full bg-white border border-[#E5E7EB] rounded-sm px-4 py-3 text-[#0A1A2F] placeholder-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1FA8E0] focus:border-[#1FA8E0] transition-all resize-none"
        />
      )}
    </div>
  );
};
